import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Download as DownloadIcon, Refresh as RefreshIcon, ExpandMore } from "@mui/icons-material";
import { useSelector } from "react-redux";
import useApiCall from "../../hooks/useApiCall";
import { usePageMeta } from "../../contexts/PageMetaContext";
import SearchInput from "../../components/SearchInput";
import { INDIGO, SLATE } from "../../constants/Colors";

const CARD_SX = {
  bgcolor: "#ffffff",
  border: `1px solid ${SLATE[200]}`,
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px -1px rgba(0,0,0,0.04)",
};

export default function DebugLogs() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [filesArray, setFilesArray] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const initialLoadDone = useRef(false);

  useEffect(() => {
    setPageMeta({ description: "View and download debug log files for this client." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id) return;
    setLoadingFiles(true);
    const url = `/v1/debug_logs/list_files?app_id=${currentApp.id}${searchQuery ? `&query=${searchQuery}` : ""}`;
    makeRequest(url, { method: "GET", authTokens: tokens })
      .then((data) => {
        const files = data?.files || [];
        setFilesArray(files);
        initialLoadDone.current = true;
      })
      .catch(() => setFilesArray([]))
      .finally(() => setLoadingFiles(false));
  }, [currentApp?.id, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open single result
  useEffect(() => {
    if (initialLoadDone.current && filesArray.length === 1 && !selectedFile && !loadingFiles) {
      viewFileContent(filesArray[0]);
    }
  }, [filesArray.length, loadingFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setSearchQuery(inputValue.toLowerCase().trim());
      setSelectedFile(null);
      setFileContent(null);
    }
  };

  const viewFileContent = async (file) => {
    setLoadingContent(true);
    setSelectedFile(file);

    try {
      const result = await makeRequest(
        `/v1/debug_logs/file_content?file_key=${encodeURIComponent(file.key)}`,
        {
          method: "GET",
          authTokens: { user_token: tokens.access_token, refresh_token: tokens.refresh_token },
        }
      );

      if (result.type === "text") {
        try {
          const jsonData = JSON.parse(result.content);
          setFileContent(formatJsonAsPlainText(jsonData));
        } catch {
          setFileContent(result.content);
        }
      } else if (result.type === "url") {
        window.open(result.url, "_blank");
        setSelectedFile(null);
        setFileContent(null);
      }
    } catch {
      setSelectedFile(null);
      setFileContent(null);
    } finally {
      setLoadingContent(false);
    }
  };

  const downloadFile = async (fileKey, fileName) => {
    try {
      const response = await makeRequest("/v1/debug_logs/download", {
        method: "POST",
        body: { file_key: fileKey },
        authTokens: tokens,
      });
      const presignedUrl = response.download_url || response.url;
      if (!presignedUrl) throw new Error("No download URL received");

      const link = document.createElement("a");
      link.href = presignedUrl;
      link.download = fileName;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const formatToLocalTime = (utcTimestamp) => {
    if (!utcTimestamp) return "N/A";
    const date = new Date(utcTimestamp);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const formatted = date.toLocaleString("en-US", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: true, timeZone,
    });
    const abbrs = {
      "Asia/Kolkata": "IST", "Asia/Calcutta": "IST",
      "America/New_York": "EST", "America/Los_Angeles": "PST",
      "America/Chicago": "CST", "America/Denver": "MST",
      "Europe/London": "GMT", "Europe/Paris": "CET",
      "Asia/Tokyo": "JST", "Australia/Sydney": "AEDT",
    };
    const tzAbbr = abbrs[timeZone] || date.toLocaleTimeString("en-US", { timeZoneName: "short", timeZone }).split(" ").pop();
    return `${formatted} ${tzAbbr}`;
  };

  const formatJsonAsPlainText = (data) => {
    let output = "";
    const separator = "----------------------------------------------------------------";
    const reversedEntries = Object.entries(data).reverse();
    let requestCount = 0;

    reversedEntries.forEach(([key, value]) => {
      if (key === "unique_request_id") return;
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        if (requestCount > 0) output += `\n${separator}\n\n`;
        requestCount++;
        Object.entries(value).forEach(([propKey, propValue]) => {
          if (propKey === "unique_request_id") return;
          if (Array.isArray(propValue)) {
            output += `${propKey}:\n`;
            const shouldNumber = propKey === "logs";
            propValue.forEach((item, idx) => {
              const prefix = shouldNumber ? `    ${idx + 1}: ` : "    ";
              if (typeof item === "string") output += `${prefix}${item}\n`;
              else if (typeof item === "object" && item !== null && item.log) output += `${prefix}${item.log}\n`;
              else if (typeof item === "object") output += `${prefix}${JSON.stringify(item)}\n`;
              else output += `${prefix}${item}\n`;
            });
          } else if (typeof propValue === "string") {
            if (propKey === "request_time" && propValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z?$/)) {
              output += `${propKey}: ${formatToLocalTime(propValue)}\n`;
            } else {
              output += `${propKey}: ${propValue}\n`;
            }
          } else if (typeof propValue === "object" && propValue !== null) {
            output += `${propKey}:\n`;
            Object.entries(propValue).forEach(([k, v]) => { output += `  ${k}: ${v}\n`; });
          } else {
            output += `${propKey}: ${propValue}\n`;
          }
        });
      }
    });
    return output;
  };

  const extractBalancedBraces = (str, startIndex) => {
    let depth = 0, start = -1, inString = false, escapeNext = false;
    for (let i = startIndex; i < str.length; i++) {
      const char = str[i];
      if (escapeNext) { escapeNext = false; continue; }
      if (char === "\\") { escapeNext = true; continue; }
      if (char === '"') { inString = !inString; continue; }
      if (!inString) {
        if (char === "{") { if (depth === 0) start = i; depth++; }
        else if (char === "}") { depth--; if (depth === 0) return { content: str.substring(start, i + 1), endIndex: i }; }
      }
    }
    return null;
  };

  const formatRubyHash = (hashStr) => {
    let formatted = "", indent = 0, inString = false, escapeNext = false;
    for (let i = 0; i < hashStr.length; i++) {
      const char = hashStr[i];
      if (escapeNext) { formatted += char; escapeNext = false; continue; }
      if (char === "\\") { formatted += char; escapeNext = true; continue; }
      if (char === '"') { inString = !inString; formatted += char; continue; }
      if (!inString) {
        if (char === "{") { formatted += "{\n"; indent += 2; formatted += " ".repeat(indent); }
        else if (char === "}") { indent -= 2; formatted += "\n" + " ".repeat(indent) + "}"; }
        else if (char === "," && hashStr[i + 1] === " ") { formatted += ",\n" + " ".repeat(indent); i++; }
        else formatted += char;
      } else { formatted += char; }
    }
    return formatted;
  };

  const parseLineSegments = (line) => {
    const segments = [];
    let currentPos = 0;
    const hashKeyPattern = /(@?\w+):\s*\{/g;
    const matches = [];
    let match;
    while ((match = hashKeyPattern.exec(line)) !== null) {
      matches.push({ key: match[1], index: match.index, colonIndex: match.index + match[1].length });
    }
    if (matches.length === 0) return [{ type: "text", content: line }];

    matches.forEach((m) => {
      if (m.index > currentPos) segments.push({ type: "text", content: line.substring(currentPos, m.index) });
      const braceResult = extractBalancedBraces(line, m.colonIndex + 1);
      if (braceResult) {
        segments.push({ type: "hash", key: m.key, rawContent: braceResult.content });
        currentPos = braceResult.endIndex + 1;
      }
    });
    if (currentPos < line.length) segments.push({ type: "text", content: line.substring(currentPos) });
    return segments;
  };

  const renderLogContent = (content) => {
    return content.split("\n").map((line, lineIndex) => {
      const segments = parseLineSegments(line);
      const hasHash = segments.some((s) => s.type === "hash");

      if (!hasHash) {
        return (
          <Box key={lineIndex} sx={{ fontFamily: "monospace", fontSize: "12px", whiteSpace: "pre-wrap" }}>
            {line}
          </Box>
        );
      }

      return (
        <Box key={lineIndex} sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", mb: 0.5 }}>
          {segments.map((seg, segIdx) => {
            if (seg.type === "text") {
              return (
                <Typography key={segIdx} component="span" sx={{ fontFamily: "monospace", fontSize: "12px", whiteSpace: "pre" }}>
                  {seg.content}
                </Typography>
              );
            }
            return (
              <Accordion
                key={segIdx}
                disableGutters
                elevation={0}
                sx={{
                  display: "inline-flex", flexDirection: "column",
                  bgcolor: "transparent", "&:before": { display: "none" },
                  margin: 0, minWidth: "200px", width: "100%",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ fontSize: "14px" }} />}
                  sx={{
                    minHeight: "auto", padding: "0 4px",
                    bgcolor: INDIGO[50], borderRadius: "4px",
                    "& .MuiAccordionSummary-content": { margin: "2px 0", fontFamily: "monospace", fontSize: "12px" },
                  }}
                >
                  <Typography sx={{ fontFamily: "monospace", fontSize: "12px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {seg.key}:{seg.rawContent}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "8px", bgcolor: "#fff", borderRadius: 1,
                    border: `1px solid ${SLATE[200]}`, mt: "4px",
                  }}
                >
                  <Box
                    component="pre"
                    sx={{ fontFamily: "monospace", fontSize: "11px", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, overflow: "auto", maxHeight: "400px" }}
                  >
                    {formatRubyHash(seg.rawContent || "")}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      );
    });
  };

  return (
    <Box sx={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 100px)",
      marginLeft: "-25px", marginRight: "-25px",
      paddingLeft: "25px", paddingRight: "25px",
      width: "calc(100% + 50px)",
    }}>
      {/* Search */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <SearchInput
          value={inputValue}
          onChange={(val) => {
            setInputValue(val);
            if (!val.trim()) {
              setSearchQuery("");
              setSelectedFile(null);
              setFileContent(null);
            }
          }}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search log files... (press Enter)"
          minWidth={360}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* Left Panel — File List */}
        <Box sx={{ ...CARD_SX, width: "350px", display: "flex", flexDirection: "column" }}>
          <Box sx={{ px: 2, py: "11px", borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50] }}>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
              Log Files {!loadingFiles && `(${filesArray.length})`}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto" }}>
            {loadingFiles ? (
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Box key={i}>
                    <Skeleton variant="text" width="75%" sx={{ fontSize: "14px" }} />
                    <Skeleton variant="text" width="50%" sx={{ fontSize: "12px" }} />
                  </Box>
                ))}
              </Box>
            ) : filesArray.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
                  No debug log files found
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {filesArray.map((file, idx) => (
                  <ListItem
                    key={idx}
                    disablePadding
                    sx={{ borderBottom: idx !== filesArray.length - 1 ? `1px solid ${SLATE[100]}` : "none" }}
                  >
                    <ListItemButton
                      selected={selectedFile?.key === file.key}
                      onClick={() => viewFileContent(file)}
                      sx={{
                        px: 2, py: "10px",
                        "&.Mui-selected": { bgcolor: INDIGO[50], "&:hover": { bgcolor: INDIGO[100] } },
                        "&:hover": { bgcolor: SLATE[50] },
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Typography sx={{
                          fontSize: "0.875rem",
                          fontWeight: selectedFile?.key === file.key ? 600 : 400,
                          color: selectedFile?.key === file.key ? INDIGO[700] : SLATE[800],
                          mb: "2px", wordBreak: "break-word",
                          }}
                        >
                          {file.filename}
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: SLATE[500] }}>
                          {formatToLocalTime(file.last_modified)}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>

        {/* Right Panel — File Content */}
        <Box sx={{ ...CARD_SX, flex: "1 1 0", minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {selectedFile ? (
            <>
              {/* Content header */}
              <Box sx={{
                px: 2, py: "10px",
                borderBottom: `1px solid ${SLATE[200]}`,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                minWidth: 0, flexShrink: 0, bgcolor: SLATE[50],
              }}>
                <Box sx={{ minWidth: 0, flex: 1, mr: 2 }}>
                  <Typography sx={{
                    fontSize: "0.875rem", fontWeight: 600, color: SLATE[900],
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {selectedFile.filename}
                  </Typography>
                  <Typography sx={{
                    fontSize: "0.75rem", color: SLATE[500], mt: "2px",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {formatToLocalTime(selectedFile.last_modified)} · {selectedFile.size}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined" size="small"
                    startIcon={<RefreshIcon sx={{ fontSize: "0.875rem" }} />}
                    onClick={() => viewFileContent(selectedFile)}
                    sx={{
                      textTransform: "none", fontSize: "0.8125rem",
                      borderColor: SLATE[300], color: SLATE[700],
                      "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] },
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined" size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: "0.875rem" }} />}
                    onClick={() => downloadFile(selectedFile.key, selectedFile.filename)}
                    sx={{
                      textTransform: "none", fontSize: "0.8125rem",
                      borderColor: INDIGO[300], color: INDIGO[700],
                      "&:hover": { borderColor: INDIGO[400], bgcolor: INDIGO[50] },
                    }}
                  >
                    Download
                  </Button>
                </Box>
              </Box>

              {/* Content body */}
              <Box sx={{ flex: 1, overflow: "auto", bgcolor: SLATE[50], p: 2 }}>
                {loadingContent ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, pt: 6 }}>
                    <CircularProgress size={18} sx={{ color: INDIGO[500] }} />
                    <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>Loading file content…</Typography>
                  </Box>
                ) : fileContent ? (
                  <Box>{renderLogContent(fileContent)}</Box>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
                    <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>No content available</Typography>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[400], textAlign: "center" }}>
                Select a file from the left panel to view its content
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
